<template>
  <div class="register-wizard">
    <el-card shadow="never" class="wizard-card">
      <!-- 步骤条按当前步骤清单重建：el-steps 的序号在挂载时定死，动态增减步骤会错位 -->
      <el-steps
        :key="stepList.map(s => s.key).join('-')"
        :active="activeIndex"
        align-center
        finish-status="success"
        class="wizard-steps"
      >
        <el-step v-for="s in stepList" :key="s.key" :title="s.title" />
      </el-steps>

      <!-- 步骤一：来源与形态 -->
      <div v-show="stage === 'source'" class="step-body">
        <el-alert
          type="info"
          :closable="false"
          class="intro-line"
          title="登记的作用：把一批图像（可含同名标注）纳入当前数据集——建立批次与图像账目，并在标注平台创建或复用同名标注项目、导入标注任务。批次名是版本发布与训练数据归集的键。"
        />
        <el-form label-width="110px" size="small">
          <el-form-item label="数据集">
            <!-- 带着数据集进来：只读展示，本批直接归到它名下 -->
            <span class="locked-dataset">{{ datasetLabel }}</span>
          </el-form-item>
          <el-form-item label="数据来源">
            <el-radio-group v-model="form.source">
              <el-radio-button label="bucket">对象存储已有前缀</el-radio-button>
              <el-radio-button label="upload">本地文件上传</el-radio-button>
            </el-radio-group>
            <span class="muted tip-inline">{{ form.source === 'bucket' ? '图像与标注已在对象存储中，按前缀登记' : '图像与标注在本机，先上传到对象存储再登记' }}</span>
          </el-form-item>
          <el-form-item label="登记形态">
            <el-radio-group v-model="form.mode" class="mode-group">
              <el-radio v-for="m in MODES" :key="m.value" :label="m.value">
                <b>{{ m.label }}</b><span class="muted mode-tip">{{ m.tip }}</span>
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item v-if="form.mode === 'image'" label="预标注">
            <el-switch v-model="form.autoPreannotate" />
            <span class="muted tip-inline">按类别注册表中已启用的类别调用 SAM3 生成预标注；结果以预测态写入，仍需人工复核</span>
          </el-form-item>
          <el-form-item label="批次名">
            <el-input v-model="form.batch" placeholder="如 pallet-1；批次是版本发布与训练数据归集的键" style="width:320px" />
          </el-form-item>
          <el-form-item label="标注项目">
            <el-input v-model="form.title" :placeholder="form.batch || '默认与批次名相同'" style="width:320px" />
            <span class="muted tip-inline">同名标注项目直接复用；项目缺少的类别自动并入标签配置</span>
          </el-form-item>
          <el-form-item v-if="form.source === 'bucket'" label="桶前缀">
            <el-input
              v-model="form.prefix"
              placeholder="如 v79：图像位于 <前缀>/…/images/，同名标注位于 <前缀>/…/labels/"
              style="width:460px"
            />
          </el-form-item>
          <el-form-item v-else label="桶前缀">
            <span class="muted">由平台生成 <b>&lt;日期&gt;/&lt;批次名&gt;</b> 前缀：图像写入 images/，标注写入 labels/</span>
          </el-form-item>
        </el-form>
        <div class="step-actions">
          <el-button type="primary" size="small" :disabled="!sourceReady" @click="goNext">下一步</el-button>
        </div>
      </div>

      <!-- 步骤二：文件上传（仅本地文件上传） -->
      <div v-show="stage === 'upload'" class="step-body">
        <el-alert
          type="info"
          :closable="false"
          title="选择本地图像文件（可多选，或直接选整个文件夹）；存在同名标注文件（.txt）时请一并选择。文件经对象存储直传，不经后端中转。"
        />
        <div class="upload-row">
          <!-- 文件夹选择走原生 input：el-upload 不支持整目录（webkitdirectory），按扩展名自动分流 -->
          <input
            ref="dirInput"
            type="file"
            webkitdirectory
            directory
            multiple
            style="display:none"
            @change="onPickFolder"
          />
          <el-button size="small" icon="el-icon-folder-opened" @click="pickFolder">选择文件夹</el-button>
          <el-upload action="#" :auto-upload="false" multiple accept=".jpg,.jpeg,.png,.bmp,.tif,.tiff,.webp" :file-list="[]" :show-file-list="false" :on-change="onPickImages">
            <el-button size="small" icon="el-icon-picture-outline">选择图像</el-button>
          </el-upload>
          <el-upload action="#" :auto-upload="false" multiple accept=".txt" :file-list="[]" :show-file-list="false" :on-change="onPickLabels">
            <el-button size="small" icon="el-icon-document">选择标注文件</el-button>
          </el-upload>
          <el-button size="small" type="primary" :loading="uploading" :disabled="!selection.length" @click="startUpload">
            上传{{ pendingImages.length ? ` ${pendingImages.length} 个图像` : '' }}{{ pendingLabels.length ? `、${pendingLabels.length} 个标注` : '' }}
          </el-button>
          <el-button v-if="selection.length" size="small" :disabled="uploading" @click="clearSelection">清空</el-button>
        </div>

        <el-progress v-if="uploading" :percentage="uploadPercent" :stroke-width="10" style="max-width:420px" />

        <!-- 已选清单：配对状态核对（缺标注/孤立标注都在这里看清） -->
        <div v-if="selection.length" class="selection-box">
          <div class="muted selection-summary">
            已选 {{ selectionSummary.images }} 个图像 · {{ selectionSummary.labels }} 个标注
            <template v-if="pair.conflict > 0"> · <span class="sel-warn">{{ pair.conflict }} 个文件名重复（同 stem 的多图或多标）</span></template>
            <br>
            配对：已配对 <b>{{ pair.matched }}</b> · 缺标注 <b :class="{ 'sel-warn': pair.missing > 0 }">{{ pair.missing }}</b> · 孤立标注 <b :class="{ 'sel-warn': pair.orphans > 0 }">{{ pair.orphans }}</b>
            · 合计 {{ humanSize(selectionSummary.bytes) }}
          </div>
          <el-table :data="selection" size="mini" border max-height="240" style="width:100%;margin-top:6px">
            <el-table-column prop="name" label="文件名" min-width="200" show-overflow-tooltip />
            <el-table-column label="类型" width="80" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="row.kind === 'image' ? '' : 'info'" size="mini">{{ row.kind === 'image' ? '图像' : '标注' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="配对" width="130" align="center">
              <template slot-scope="{ row }">
                <el-tag v-if="row.status === 'sending'" size="mini">上传中</el-tag>
                <el-tag v-else-if="row.status === 'done'" type="success" size="mini">已上传</el-tag>
                <el-tag v-else-if="row.status === 'failed'" type="danger" size="mini">失败</el-tag>
                <el-tag v-else-if="row.pair === 'matched'" type="success" size="mini">已配对</el-tag>
                <el-tag v-else-if="row.pair === 'missing'" type="warning" size="mini">缺标注</el-tag>
                <el-tag v-else type="info" size="mini">孤立（忽略）</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="大小" width="90" align="right">
              <template slot-scope="{ row }">{{ humanSize(row.size) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="70" align="center">
              <template slot-scope="{ row }">
                <el-button type="text" size="mini" :disabled="uploading" @click="removeItem(row)">移除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div v-if="uploadedItems.length" class="upload-result">
          <div class="muted">已上传 {{ uploadedItems.length }} 个对象；前缀 <span class="mono">{{ uploadPrefix }}/</span></div>
          <el-table :data="uploadedItems" size="mini" border max-height="240" style="width:100%;margin-top:8px">
            <el-table-column prop="name" label="文件名" min-width="200" show-overflow-tooltip />
            <el-table-column prop="kind" label="类型" width="90" align="center" />
            <el-table-column prop="key" label="对象键" min-width="240" show-overflow-tooltip />
          </el-table>
        </div>

        <div class="step-actions">
          <el-button size="small" @click="stage = 'source'">上一步</el-button>
          <el-button type="primary" size="small" :disabled="uploading || uploadedItems.length === 0" @click="goNext">下一步</el-button>
        </div>
      </div>

      <!-- 步骤三：登记预检 -->
      <div v-show="stage === 'dry'" class="step-body" v-loading="dryLoading" element-loading-text="正在预检（读取对象存储、抽检标注与图像解码）…">
        <div class="dry-actions">
          <el-button type="primary" size="small" :loading="dryLoading" @click="runDry">{{ report ? '重新预检' : '执行预检' }}</el-button>
          <span class="muted">只读预检：图像与标注配对情况、标注类别编号范围、图像可解码性；不写入任何数据</span>
        </div>

        <!-- 空态：进来就说明这一步在干什么、会看哪些项，避免一片空白 -->
        <el-alert
          v-if="!report && !dryLoading"
          type="info"
          :closable="false"
          title="还没预检。点「执行预检」开始——它会读一遍待登记的对象存储前缀并核对下面这些项，不改动任何数据："
          class="alert-line"
        >
          <div class="dry-hint">
            ① 图片与同名 txt 的配对情况（缺标注、孤立标注会点名）<br>
            ② 标注 txt 抽检：格式非法行、类别编号是否超出注册表启用类别（分割多边形与检测框都认，检测框按矩形导入）<br>
            ③ 图片抽检：能否正常解码<br>
            ④ 汇总：对象数 / 图像数 / 配对率，并给出是否允许提交登记的结论
          </div>
        </el-alert>

        <template v-if="report">
          <el-alert v-for="(b, i) in report.blockers" :key="`b${i}`" type="error" :closable="false" :title="b" class="alert-line" />
          <el-alert v-for="(w, i) in report.warnings" :key="`w${i}`" type="warning" :closable="false" :title="w" class="alert-line" />

          <el-descriptions :column="3" border size="small" class="report-block">
            <el-descriptions-item label="登记形态">{{ modeLabel(report.mode) }}</el-descriptions-item>
            <el-descriptions-item label="批次名">{{ report.batch }}</el-descriptions-item>
            <el-descriptions-item label="标注项目">{{ report.title }}</el-descriptions-item>
            <el-descriptions-item label="桶前缀"><span class="mono">{{ report.source_prefix }}</span></el-descriptions-item>
            <el-descriptions-item label="注册表启用类别">{{ report.class_names.join('、') || '-' }}</el-descriptions-item>
            <el-descriptions-item label="数据来源">{{ report.uploaded ? '本地文件上传' : '对象存储已有前缀' }}</el-descriptions-item>
            <el-descriptions-item label="对象数 / 图像数">{{ report.counts.objects }} / {{ report.counts.images }}</el-descriptions-item>
            <el-descriptions-item label="已配对标注 / 缺失标注">{{ report.counts.matched }} / {{ report.counts.missing }}</el-descriptions-item>
            <el-descriptions-item label="孤立标注 / 其他对象">{{ report.counts.orphans }} / {{ report.counts.extras }}</el-descriptions-item>
            <el-descriptions-item label="标注抽检">抽 {{ report.txt.checked }} 个 txt：非法行 {{ report.txt.bad_lines }}，类别编号越界 {{ report.txt.out_of_range }}，含置信度 {{ report.txt.conf_lines }}，检测框 {{ report.txt.box_lines || 0 }} 行（按矩形导入）</el-descriptions-item>
            <el-descriptions-item label="图像解码抽检">抽 {{ report.decode.checked }} 张：失败 {{ report.decode.failures.length }}</el-descriptions-item>
            <el-descriptions-item label="空标注 / 缺标（抽检口径）">{{ report.summary.empty }} / {{ report.summary.missing }}</el-descriptions-item>
          </el-descriptions>

          <div class="by-class">
            <span class="muted">按类别图像数：</span>
            <el-tag v-for="(n, name) in report.summary.byClass" :key="name" size="small" class="class-tag">{{ name }} {{ n }}</el-tag>
            <span v-if="!Object.keys(report.summary.byClass).length" class="muted">-</span>
          </div>
        </template>

        <div class="step-actions">
          <el-button size="small" @click="goBack">上一步</el-button>
          <el-button
            type="primary"
            size="small"
            :disabled="!report || !report.ok"
            :loading="creating"
            @click="createRegister"
          >提交登记</el-button>
        </div>
      </div>

      <!-- 步骤四：登记执行 -->
      <div v-show="stage === 'run'" class="step-body">
        <template v-if="job">
          <el-descriptions :column="2" border size="small" class="report-block">
            <el-descriptions-item label="登记任务">{{ job.id }}</el-descriptions-item>
            <el-descriptions-item label="状态">
              <el-tag :type="statusType(job.status)" size="small">{{ job.status }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="批次名">{{ created.batch }}</el-descriptions-item>
            <el-descriptions-item label="登记形态">{{ modeLabel(created.mode) }}</el-descriptions-item>
            <el-descriptions-item label="桶前缀"><span class="mono">{{ created.prefix }}/</span></el-descriptions-item>
            <el-descriptions-item label="标注项目">
              <a v-if="created.project_url" :href="created.project_url" target="_blank" rel="noopener">{{ created.project_title }}</a>
              <span v-else>{{ created.project_title }}（#{{ created.project_id }}）</span>
            </el-descriptions-item>
            <el-descriptions-item label="已登记图像数 / 任务数">{{ job.image_count || 0 }} / {{ job.task_count || 0 }}</el-descriptions-item>
            <el-descriptions-item label="说明">{{ job.error_message || '-' }}</el-descriptions-item>
          </el-descriptions>

          <el-alert v-if="job.status === 'completed'" type="success" :closable="false" title="登记完成：图像账目已写入数据集，标注任务已导入标注平台" class="alert-line" />
          <el-alert v-else-if="job.status === 'failed'" type="error" :closable="false" :title="`登记失败：${job.error_message || '详情见后端日志'}`" class="alert-line" />
          <el-alert v-else type="info" :closable="false" :title="polling ? '登记执行中，每 3 秒自动刷新' : '等待执行器接手（10 秒内）'" class="alert-line" />

          <!-- 勾了预标注才出现：任务是否真的派发、起止时间、结果是否写回标注平台看这一块 -->
          <div v-if="preannotateWanted" class="preannotate-block">
            <div class="preannotate-line">
              <span class="preannotate-lab">预标注任务</span>
              <span class="preannotate-text">{{ preannotateLine }}</span>
            </div>
            <div v-if="preannotateReceipt" class="preannotate-receipt">{{ preannotateReceipt }}</div>
          </div>
        </template>

        <div class="step-actions">
          <el-button v-if="job && job.status === 'failed'" size="small" :loading="creating" @click="createRegister">重试登记</el-button>
          <el-button size="small" @click="resetAll">继续登记下一批</el-button>
          <el-button size="small" type="primary" @click="$emit('close')">完成</el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import {
  fetchDatasets,
  previewDatasetRegister,
  createDatasetRegister,
  fetchDatasetRegisterUploadUrl,
  fetchDatasetJob,
} from '../../api/dataset';
import { resolveNetworkType } from '../../utils/network';
import { preAnnotateStatusText, preAnnotateReceiptText } from '../../utils/preannotate';

const MODES = [
  { value: 'image', label: '仅图像', tip: '只导入图像，标注在标注平台由人工完成（可勾选登记后自动预标注）' },
  { value: 'preannotate', label: '图像＋预测标注', tip: '导入图像与同名预测标注 txt，在标注平台作为待审核的预测结果' },
  { value: 'truth', label: '图像＋真值标注', tip: '导入图像与同名真值标注 txt，导入即视为已确认标注，可直接参与版本发布' },
];

const IMAGE_RE = /\.(jpg|jpeg|png|bmp|tif|tiff|webp)$/i;
const POLL_MS = 3000;
const UPLOAD_URL_MAX = 200; // 直传链接接口单次文件名上限

export default {
  name: 'RegisterWizard',

  props: {
    /** 带着数据集进来（弹窗入口传当前数据集，null＝默认数据集）：本批直接归到它名下 */
    presetDatasetId: { type: Number, default: null },
  },

  data() {
    return {
      MODES,
      datasets: [],
      stage: 'source',
      form: {
        datasetId: null,
        source: 'bucket',
        mode: 'image',
        autoPreannotate: false,
        batch: '',
        title: '',
        prefix: '',
      },
      pendingImages: [],
      pendingLabels: [],
      uploadedItems: [],
      uploadPrefix: '',
      uploading: false,
      uploadPercent: 0,
      /** 文件名 → 上传状态（sending/done/failed），清单里逐行显示 */
      uploadState: {},
      dryLoading: false,
      report: null,
      creating: false,
      job: null,
      /** 本批的预标注任务（勾了预标注才有，登记完成时任务号随登记任务下发） */
      preJob: null,
      created: {},
      polling: false,
      timer: null,
      pollStartedAt: 0,
    };
  },

  computed: {
    /** 只读态显示的数据集名：优先名册里的名字，名册还没到时退回标识 */
    datasetLabel() {
      const hit = this.datasets.find(d => d.id === this.form.datasetId);
      if (hit) return hit.name ? `${hit.name}（${hit.slug}）` : hit.slug;
      return this.form.datasetId ? `#${this.form.datasetId}` : '默认数据集';
    },

    stepList() {
      const steps = [{ key: 'source', title: '来源与形态' }];
      if (this.form.source === 'upload') steps.push({ key: 'upload', title: '文件上传' });
      steps.push({ key: 'dry', title: '登记预检' }, { key: 'run', title: '登记执行' });
      return steps;
    },

    activeIndex() {
      const i = this.stepList.findIndex(s => s.key === this.stage);
      return i < 0 ? 0 : i;
    },

    sourceReady() {
      return Boolean(this.form.batch.trim());
    },

    /** 已选清单（图像在前、标注在后）：类型/大小/配对状态/上传状态，供核对与逐行移除 */
    selection() {
      const stemOf = name => name.replace(/\.[^.]*$/, '');
      const labelStems = new Set(this.pendingLabels.map(f => stemOf(f.name)));
      const imageStems = new Set(this.pendingImages.map(f => stemOf(f.name)));
      const rows = [];
      for (const f of this.pendingImages) {
        rows.push({
          name: f.name,
          kind: 'image',
          size: f.size,
          pair: labelStems.has(stemOf(f.name)) ? 'matched' : 'missing',
          status: this.uploadState[f.name] || '',
          ref: f,
        });
      }
      for (const f of this.pendingLabels) {
        rows.push({
          name: f.name,
          kind: 'label',
          size: f.size,
          pair: imageStems.has(stemOf(f.name)) ? 'matched' : 'orphan',
          status: this.uploadState[f.name] || '',
          ref: f,
        });
      }
      return rows;
    },

    /** 配对汇总：与后端 pairObjects 同口径（同 stem 配对；孤立标注登记时忽略） */
    pair() {
      const rows = this.selection;
      const matched = rows.filter(r => r.kind === 'image' && r.pair === 'matched').length;
      const missing = rows.filter(r => r.kind === 'image' && r.pair === 'missing').length;
      const orphans = rows.filter(r => r.kind === 'label' && r.pair === 'orphan').length;
      // 同 stem 多文件（如 a.jpg 与 a.png，或两个目录下的同名文件）：登记时配对键相同会互相覆盖
      const stemCount = new Map();
      for (const r of rows) {
        const stem = r.name.replace(/\.[^.]*$/, '');
        stemCount.set(stem, (stemCount.get(stem) || 0) + 1);
      }
      let conflict = 0;
      for (const n of stemCount.values()) if (n > 1) conflict++;
      return { matched, missing, orphans, conflict };
    },

    selectionSummary() {
      const rows = this.selection;
      return {
        images: rows.filter(r => r.kind === 'image').length,
        labels: rows.filter(r => r.kind === 'label').length,
        bytes: rows.reduce((s, r) => s + (Number(r.size) || 0), 0),
      };
    },

    /** 建任务接口回的当次表单快照：勾了预标注才展示预标注块 */
    preannotateWanted() {
      const v = this.created.auto_preannotate;
      return v === true || v === 'true';
    },

    /** 预标注任务行：任务号还没到时说明卡在哪一步，到了就用生命周期文案（派发/起止/失败原因） */
    preannotateLine() {
      if (this.preJob) return preAnnotateStatusText(this.preJob);
      const job = this.job || {};
      // 登记任务本身失败就轮不到预标注，别把「未勾类别」的提示错安在失败上
      if (job.status === 'failed') return '登记失败，预标注未派发';
      if (job.status !== 'completed') return '等待登记完成';
      return '未派发（类别注册表没有启用的类别）';
    },

    preannotateReceipt() {
      return preAnnotateReceiptText((this.preJob || {}).receipt);
    },

    /** 预标注任务还在跑：登记任务跑完不等于收工，要接着跟它 */
    preannotatePending() {
      const p = this.preJob || {};
      return Boolean(p.id) && p.status !== 'completed' && p.status !== 'failed';
    },
  },

  created() {
    this.form.datasetId = this.presetDatasetId;
    this.loadDatasets();
  },

  watch: {
    // 影响预检结论的入参一变，旧报告立即作废（否则拿旧结论提交登记）
    'form.mode'() { this.invalidateReport(); },
    'form.source'() { this.invalidateReport(); },
    'form.prefix'() { this.invalidateReport(); },
    'form.batch'() { this.invalidateReport(); },
  },

  beforeDestroy() {
    this.stopPoll();
  },

  methods: {
    async loadDatasets() {
      try {
        const data = (await fetchDatasets()).data || {};
        this.datasets = data.items || [];
        // 名册里名字给只读那一行用；带进来的数据集优先，没有才回退后端默认
        if (this.form.datasetId != null) return;
        const fallback = this.datasets.find(d => d.slug === data.default_slug);
        if (fallback) this.form.datasetId = fallback.id;
      } catch (err) {
        // 名册拿不到不挡登记：不选数据集时后端按默认数据集走
        this.datasets = [];
      }
    },

    modeLabel(mode) {
      const hit = MODES.find(m => m.value === mode);
      return hit ? hit.label : mode;
    },

    statusType(status) {
      return { completed: 'success', failed: 'danger', processing: 'warning' }[status] || 'info';
    },

    goNext() {
      const idx = this.stepList.findIndex(s => s.key === this.stage);
      const next = this.stepList[idx + 1];
      if (next) this.stage = next.key;
      // 进入预检步骤自动跑一次（结论仍可点按钮重跑），避免进来一片空白
      if (this.stage === 'dry' && !this.report && !this.dryLoading) this.runDry();
    },

    goBack() {
      const idx = this.stepList.findIndex(s => s.key === this.stage);
      const prev = this.stepList[idx - 1];
      if (prev) this.stage = prev.key;
    },

    /** 登记入参：桶模式带前缀；上传模式带已上传的前缀（跨零点也指同一处） */
    buildParams() {
      const params = {
        dataset_id: this.form.datasetId || undefined,
        mode: this.form.mode,
        batch: this.form.batch.trim(),
        title: this.form.title.trim() || undefined,
        auto_preannotate: this.form.mode === 'image' && this.form.autoPreannotate,
      };
      const prefix = this.form.source === 'bucket' ? this.form.prefix.trim() : this.uploadPrefix;
      if (prefix) params.source_prefix = prefix;
      return params;
    },

    onPickImages(file, fileList) {
      // el-upload 每选一个回调一次，最后一次给全量列表；按扩展名与重名去重
      const seen = new Set();
      this.pendingImages = fileList
        .filter(f => IMAGE_RE.test(f.name))
        .filter(f => (seen.has(f.name) ? false : seen.add(f.name)))
        .map(f => f.raw);
    },

    onPickLabels(file, fileList) {
      const seen = new Set();
      this.pendingLabels = fileList
        .filter(f => /\.txt$/i.test(f.name))
        .filter(f => (seen.has(f.name) ? false : seen.add(f.name)))
        .map(f => f.raw);
    },

    pickFolder() {
      this.$refs.dirInput.click();
    },

    /**
     * 整目录选择：按扩展名分流成图像/标注两拨（同名去重，后选的覆盖先选的）；
     * 目录内的子目录结构不进桶（对象键按文件名平铺），重名会提示。
     */
    onPickFolder(ev) {
      const files = Array.from(ev.target.files || []);
      ev.target.value = ''; // 允许再次选同一目录
      const images = files.filter(f => IMAGE_RE.test(f.name));
      const labels = files.filter(f => /\.txt$/i.test(f.name));
      const other = files.length - images.length - labels.length;

      const merge = (cur, add) => {
        const map = new Map(cur.map(f => [f.name, f]));
        const dup = [];
        for (const f of add) {
          if (map.has(f.name) && map.get(f.name) !== f) dup.push(f.name);
          map.set(f.name, f);
        }
        return { list: [...map.values()], dup };
      };
      const im = merge(this.pendingImages, images);
      const lb = merge(this.pendingLabels, labels);
      this.pendingImages = im.list;
      this.pendingLabels = lb.list;

      const dupCount = im.dup.length + lb.dup.length;
      const parts = [`已选 ${im.list.length} 个图像、${lb.list.length} 个标注`];
      if (other > 0) parts.push(`${other} 个其他文件已忽略`);
      if (dupCount > 0) parts.push(`${dupCount} 个重名文件已按后选的保留`);
      this.$message.info(parts.join('；'));
    },

    /** 入参变了就把旧预检报告作废（重新进预检步骤会再跑一次） */
    invalidateReport() {
      if (this.stage !== 'dry') this.report = null;
    },

    humanSize(bytes) {
      const n = Number(bytes) || 0;
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / 1024 / 1024).toFixed(1)} MB`;
    },

    removeItem(row) {
      if (row.kind === 'image') this.pendingImages = this.pendingImages.filter(f => f !== row.ref);
      else this.pendingLabels = this.pendingLabels.filter(f => f !== row.ref);
      delete this.uploadState[row.name];
    },

    clearSelection() {
      this.pendingImages = [];
      this.pendingLabels = [];
      this.uploadState = {};
    },

    /**
     * 取直传链接后并发 PUT：逐行显示上传状态，成功的移出待传队列；
     * 单次文件数上限 UPLOAD_URL_MAX，超出的按段申请；
     * 中途失败只留失败的（其余已传的不动，重试只补剩下的，不重传全量）。
     */
    async startUpload() {
      if (!this.pendingImages.length && !this.pendingLabels.length) return;
      this.uploading = true;
      this.uploadPercent = 0;
      const jobs = [];
      if (this.pendingImages.length) jobs.push({ kind: 'image', files: this.pendingImages });
      if (this.pendingLabels.length) jobs.push({ kind: 'label', files: this.pendingLabels });
      const total = this.pendingImages.length + this.pendingLabels.length;
      let done = 0;
      const failed = new Set();
      try {
        for (const job of jobs) {
          for (let start = 0; start < job.files.length; start += UPLOAD_URL_MAX) {
            const chunk = job.files.slice(start, start + UPLOAD_URL_MAX);
            const res = await fetchDatasetRegisterUploadUrl({
              batch: this.form.batch.trim(),
              kind: job.kind,
              file_names: chunk.map(f => f.name),
              network_type: resolveNetworkType(),
            });
            const items = (res.data && res.data.items) || [];
            this.uploadPrefix = (res.data && res.data.prefix) || this.uploadPrefix;
            // 并发 4 路；每个文件独立 try：单张失败不拖垮整批，记下失败行留待重试
            const tasks = items.map((item, i) => ({ item, file: chunk[i], kind: job.kind }));
            let next = 0;
            const pool = Array.from({ length: Math.min(4, tasks.length) }, async () => {
              while (next < tasks.length) {
                const t = tasks[next++];
                this.$set(this.uploadState, t.file.name, 'sending');
                try {
                  await this.putFile(t.item.url, t.file);
                  this.$set(this.uploadState, t.file.name, 'done');
                  this.uploadedItems.push({
                    name: t.file.name,
                    kind: t.kind === 'image' ? 'images' : 'labels',
                    key: t.item.key,
                  });
                } catch (err) {
                  this.$set(this.uploadState, t.file.name, 'failed');
                  failed.add(t.file);
                }
                done++;
                this.uploadPercent = Math.round((done / total) * 100);
              }
            });
            await Promise.all(pool);
            // 这一段里成功的直接移出待传队列，重试只剩失败行
            if (job.kind === 'image') this.pendingImages = this.pendingImages.filter(f => this.uploadState[f.name] !== 'done');
            else this.pendingLabels = this.pendingLabels.filter(f => this.uploadState[f.name] !== 'done');
          }
        }
        if (failed.size === 0) {
          this.clearSelection();
          this.$message.success(`上传完成：${this.uploadedItems.length} 个对象`);
        } else {
          this.$message.warning(`上传完成：成功 ${total - failed.size} 个、失败 ${failed.size} 个；点「上传」可只补传失败的`);
        }
      } catch (err) {
        // 链接申请失败等整批性错误：错误提示已由 request 拦截器统一弹出
      } finally {
        this.uploading = false;
      }
    },

    /** 浏览器直传对象存储（不经过后端，不套业务响应外壳） */
    putFile(url, file) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);
        xhr.timeout = 600000;
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`直传失败（对象存储返回 ${xhr.status}）`)));
        xhr.onerror = () => reject(new Error('直传失败（网络错误）'));
        xhr.ontimeout = () => reject(new Error('直传超时'));
        xhr.send(file);
      });
    },

    async runDry() {
      this.dryLoading = true;
      try {
        const res = await previewDatasetRegister(this.buildParams());
        this.report = res.data;
      } catch (err) {
        this.report = null;
      } finally {
        this.dryLoading = false;
      }
    },

    async createRegister() {
      this.creating = true;
      try {
        const res = await createDatasetRegister(this.buildParams());
        this.created = res.data || {};
        this.job = { id: this.created.job_id, status: 'pending', image_count: 0, task_count: 0 };
        this.stage = 'run';
        this.$message.success('登记任务已建，执行器接手后进度会自动刷新');
        this.$emit('registered'); // 外层刷新批次/版本列表
        this.startPoll();
      } catch (err) {
        // 错误提示已由 request 拦截器统一弹出
      } finally {
        this.creating = false;
      }
    },

    startPoll() {
      this.stopPoll();
      this.pollStartedAt = Date.now();
      this.polling = true;
      this.timer = setInterval(() => this.pollJob(), POLL_MS);
      this.pollJob();
    },

    stopPoll() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
      this.polling = false;
    },

    async pollJob() {
      if (!this.job || !this.job.id) return;
      // 10 分钟还没结束就停轮询，避免页面挂着一直打接口
      if (Date.now() - this.pollStartedAt > 10 * 60 * 1000) {
        this.stopPoll();
        return;
      }
      try {
        const res = await fetchDatasetJob(this.job.id);
        const job = res.data || {};
        this.job = { ...this.job, ...job };
        await this.pollPreannotate();
        // 登记任务跑完不等于收工：勾了预标注还要跟到预标注任务的终态
        if ((job.status === 'completed' || job.status === 'failed') && !this.preannotatePending) this.stopPoll();
      } catch (err) {
        // 单次轮询失败不停表，下一轮再试
      }
    },

    /** 拉一次本批的预标注任务：任务号在登记完成时写进登记任务的 options */
    async pollPreannotate() {
      const id = (this.job.options && this.job.options.preannotate_job_id) || (this.preJob && this.preJob.id);
      if (!id) return;
      try {
        const res = await fetchDatasetJob(id);
        this.preJob = { ...(this.preJob || {}), ...(res.data || {}) };
      } catch (err) {
        // 单次失败不停表，下一轮再试
      }
    },

    resetAll() {
      this.stopPoll();
      Object.assign(this.form, {
        datasetId: this.presetDatasetId,
        source: 'bucket',
        mode: 'image',
        autoPreannotate: false,
        batch: '',
        title: '',
        prefix: '',
      });
      this.pendingImages = [];
      this.pendingLabels = [];
      this.uploadedItems = [];
      this.uploadPrefix = '';
      this.uploadPercent = 0;
      this.uploadState = {};
      this.report = null;
      this.job = null;
      this.preJob = null;
      this.created = {};
      this.stage = 'source';
    },
  },
};
</script>

<style scoped>
/* 弹窗内嵌：卡片只作框，不留外边距 */
.register-wizard {
  padding: 0;
}
.locked-dataset {
  font-weight: 600;
}
.wizard-steps {
  margin: 8px 0 24px;
}
.step-body {
  max-width: 980px;
}
.mode-group .el-radio {
  display: block;
  margin: 6px 0;
  white-space: normal;
}
.mode-tip {
  margin-left: 8px;
}
.tip-inline {
  margin-left: 10px;
}
.upload-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0;
}
.upload-result {
  margin-top: 8px;
}
.selection-box {
  margin-top: 10px;
}
.selection-summary {
  line-height: 1.9;
}
.sel-warn {
  color: #e6a23c;
}
.dry-hint {
  margin-top: 6px;
  line-height: 1.9;
}
.dry-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.alert-line {
  margin-bottom: 8px;
}
.intro-line {
  margin-bottom: 16px;
}
.report-block {
  margin: 14px 0;
}
.by-class {
  margin-bottom: 8px;
}
.class-tag {
  margin-right: 6px;
}
.step-actions {
  margin-top: 20px;
  display: flex;
  gap: 10px;
}
/* 预标注任务行：登记完成后的第二段生命周期，左竖线标识从属于登记任务 */
.preannotate-block {
  margin: 4px 0 10px;
  padding: 8px 12px;
  border-left: 3px solid #409eff;
  background: #f4f8ff;
}
.preannotate-lab {
  margin-right: 10px;
  color: #606266;
}
.preannotate-text {
  font-weight: 600;
  /* 失败文案带服务端日志尾巴（可达 2000 字符），保留换行并强制断行，不撑破区块 */
  white-space: pre-wrap;
  word-break: break-all;
}
.preannotate-receipt {
  margin-top: 6px;
  color: #606266;
}
.muted {
  color: #909399;
}
.mono {
  font-family: Menlo, Consolas, monospace;
}
</style>
